import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError, formatCurrency, formatDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useAgency } from "@/context/AgencyContext";
import { StatusBadge } from "@/components/StatusBadge";
import { PolicyTypeBadge } from "@/components/PolicyTypeBadge";
import { ChatPanel } from "@/components/ChatPanel";
import { LiveClock } from "@/components/LiveClock";
import { Briefcase, LogOut, ShieldCheck, Car, MessageSquare, Settings } from "lucide-react";
import { toast } from "sonner";
import { logger } from "@/lib/logger";

const TYPE_LABEL = {
  auto: "Auto", casa: "Casa", vita: "Vita", salute: "Salute",
  infortuni: "Infortuni", viaggio: "Viaggio", responsabilita_civile: "RC",
};

export default function BrokerArea() {
  const { user, logout } = useAuth();
  const { agency } = useAgency();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [conv, setConv] = useState(null);
  const [tab, setTab] = useState("messaggi");

  useEffect(() => {
    api.get("/my/broker-portfolio")
      .then((r) => setData(r.data))
      .catch((e) => { logger.error(e); toast.error(formatApiError(e)); });
    api.get("/my/conversation")
      .then((r) => setConv(r.data))
      .catch((e) => toast.error(formatApiError(e)));
  }, []);

  const onLogout = async () => {
    await logout();
    navigate("/login");
  };

  if (!data || !conv) return <div className="min-h-screen flex items-center justify-center text-slate-400">Caricamento…</div>;

  const totalPremium = data.policies.reduce((a, p) => a + (p.premium || 0), 0);
  const totalCommission = data.policies.reduce((a, p) => a + (p.commission_amount || 0), 0);

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="broker-area">
      <header className="bg-[#0A192F] text-white">
        <div className="max-w-5xl mx-auto px-6 py-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="h-9 w-9 rounded-md bg-emerald-600 flex items-center justify-center overflow-hidden">
              {agency?.logo_url ? (
                <img src={agency.logo_url} alt="logo" className="h-full w-full object-cover" />
              ) : (
                <Briefcase className="h-5 w-5" />
              )}
            </div>
            <div>
              <div className="font-display font-semibold tracking-tight">{agency?.name || "Assicura"}</div>
              <div className="text-xs text-slate-400">Area Broker</div>
              <div data-testid="role-badge-portal" className="mt-2 inline-flex items-center text-[10px] uppercase tracking-[0.15em] font-bold px-2 py-0.5 rounded border bg-emerald-400/15 text-emerald-200 border-emerald-300/30">
                🤝 Broker / Agenzia
              </div>
            </div>
          </div>
          <button
            data-testid="broker-logout"
            onClick={onLogout}
            className="text-xs px-3 py-1.5 rounded bg-white/10 hover:bg-white/20 inline-flex items-center gap-1.5"
          >
            <LogOut className="h-3.5 w-3.5" /> Esci
          </button>
        </div>
        <div className="max-w-5xl mx-auto px-6 pb-2">
          <div className="bg-white/5 rounded px-3 py-1.5 inline-block text-slate-300">
            <LiveClock inline />
          </div>
        </div>
        <div className="max-w-5xl mx-auto px-6 pb-8">
          <p className="text-xs uppercase tracking-[0.2em] text-emerald-300 mb-2">Portafoglio assegnato</p>
          <h1 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight">{data.broker?.name || user?.name}</h1>
          {data.broker?.contact_person && (
            <p className="text-sm text-slate-300 mt-1">Riferimento: {data.broker.contact_person}</p>
          )}
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 -mt-6 pb-12">
        <div className="bg-white border border-slate-200 rounded-lg p-3 flex gap-2 mb-4">
          <button
            data-testid="tab-messaggi"
            onClick={() => setTab("messaggi")}
            className={`flex-1 text-sm py-2 rounded-md transition-colors flex items-center justify-center gap-2 ${
              tab === "messaggi" ? "bg-emerald-600 text-white" : "text-slate-600 hover:bg-slate-100"
            }`}
          >
            <MessageSquare className="h-4 w-4" />
            Messaggi
            {conv?.unread_count > 0 && (
              <span className="bg-red-500 text-white text-[10px] font-semibold rounded-full px-1.5 min-w-[18px] h-[18px] flex items-center justify-center">{conv.unread_count}</span>
            )}
          </button>
          <button
            data-testid="tab-polizze"
            onClick={() => setTab("polizze")}
            className={`flex-1 text-sm py-2 rounded-md transition-colors flex items-center justify-center gap-2 ${
              tab === "polizze" ? "bg-emerald-600 text-white" : "text-slate-600 hover:bg-slate-100"
            }`}
          >
            <ShieldCheck className="h-4 w-4" />
            Polizze ({data.policies.length})
          </button>
          <button
            data-testid="tab-preferenze"
            onClick={() => setTab("preferenze")}
            className={`flex-1 text-sm py-2 rounded-md transition-colors flex items-center justify-center gap-2 ${
              tab === "preferenze" ? "bg-emerald-600 text-white" : "text-slate-600 hover:bg-slate-100"
            }`}
          >
            <Settings className="h-4 w-4" />
            Preferenze
          </button>
        </div>

        {tab === "messaggi" && (
          <div className="bg-white border border-slate-200 rounded-lg overflow-hidden flex flex-col" style={{ minHeight: "70vh" }}>
            <ChatPanel
              conversationId={conv.id}
              partnerName={agency?.name || "Assicura"}
              partnerType="agency"
              currentUser={user}
            />
          </div>
        )}

        {tab === "polizze" && (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
              <div className="bg-white border border-slate-200 rounded-lg p-5">
                <p className="text-xs uppercase tracking-wide text-slate-500">Polizze</p>
                <p className="text-3xl font-display font-semibold mt-1 tabular-nums" data-testid="broker-policies-count">{data.policies.length}</p>
              </div>
              <div className="bg-white border border-slate-200 rounded-lg p-5">
                <p className="text-xs uppercase tracking-wide text-slate-500">Premi totali</p>
                <p className="text-3xl font-display font-semibold mt-1 tabular-nums">{formatCurrency(totalPremium)}</p>
              </div>
              <div className="bg-white border border-slate-200 rounded-lg p-5">
                <p className="text-xs uppercase tracking-wide text-slate-500">Provvigioni</p>
                <p className="text-3xl font-display font-semibold mt-1 tabular-nums text-emerald-700">{formatCurrency(totalCommission)}</p>
              </div>
            </div>

            <section>
              <h2 className="font-display text-xl font-semibold mb-4">Polizze a me assegnate</h2>
              <div className="space-y-3" data-testid="broker-policies-list">
                {data.policies.length === 0 && (
                  <div className="bg-white border border-dashed border-slate-300 rounded-lg p-10 text-center text-slate-400">
                    Nessuna polizza assegnata.
                  </div>
                )}
                {data.policies.map((p) => (
                  <div key={p.id} className="bg-white border border-slate-200 rounded-lg p-5" data-testid={`broker-policy-${p.id}`}>
                    <div className="flex items-start justify-between gap-3 flex-wrap">
                      <div>
                        <div className="flex items-center gap-2 flex-wrap">
                          <PolicyTypeBadge code={p.type} size="md" testid={`broker-type-badge-${p.id}`} />
                          <StatusBadge status={p.status} />
                        </div>
                        <p className="text-xs text-slate-500 mt-0.5">{p.company} · n° {p.policy_number} · cliente {p.client_name}</p>
                        {p.vehicle_plate && (
                          <p className="text-xs text-slate-500 mt-1 inline-flex items-center gap-1.5">
                            <Car className="h-3 w-3" />
                            <span className="font-mono">{p.vehicle_plate}</span>
                            {p.vehicle_brand && <span>· {p.vehicle_brand} {p.vehicle_model}</span>}
                          </p>
                        )}
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 text-sm">
                        <div>
                          <p className="text-xs uppercase tracking-wide text-slate-400">Premio</p>
                          <p className="font-medium tabular-nums mt-0.5">{formatCurrency(p.premium)}</p>
                        </div>
                        <div>
                          <p className="text-xs uppercase tracking-wide text-slate-400">Provvigione</p>
                          <p className="font-medium tabular-nums mt-0.5 text-emerald-700">{formatCurrency(p.commission_amount || 0)}</p>
                        </div>
                        <div>
                          <p className="text-xs uppercase tracking-wide text-slate-400">Scadenza</p>
                          <p className="font-medium tabular-nums mt-0.5">{formatDate(p.end_date)}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          </>
        )}

        {tab === "preferenze" && (
          <section className="bg-white border border-slate-200 rounded-lg p-6 space-y-5" data-testid="broker-preferences">
            <div>
              <h3 className="font-display text-lg font-semibold text-slate-900">Preferenze di privacy</h3>
              <p className="text-sm text-slate-500">Configura come ricevere i preventivi futuri dall&apos;agenzia.</p>
            </div>
            <label className="flex items-start gap-3 cursor-pointer border border-slate-200 rounded-md p-4 hover:bg-slate-50 transition-colors">
              <input
                type="checkbox"
                data-testid="broker-pref-share-contacts"
                checked={!!data.broker?.share_contacts_default}
                onChange={async (e) => {
                  const checked = e.target.checked;
                  try {
                    await api.put("/my/broker-share-preference", { share_contacts_default: checked });
                    setData((d) => ({ ...d, broker: { ...d.broker, share_contacts_default: checked } }));
                    toast.success(checked
                      ? "Riceverai i recapiti del cliente nei prossimi preventivi"
                      : "Niente recapiti cliente nei prossimi preventivi");
                  } catch (err) {
                    toast.error(formatApiError(err));
                  }
                }}
                className="mt-1 h-4 w-4 cursor-pointer accent-emerald-600"
              />
              <div className="flex-1">
                <div className="font-medium text-slate-900">Includi sempre email e telefono del cliente</div>
                <p className="text-xs text-slate-500 mt-1">
                  Se attivo, l&apos;agenzia includerà automaticamente i recapiti del cliente
                  (email, telefono, cellulare) nei preventivi che ti invierà. Puoi cambiare la preferenza in qualsiasi momento.
                </p>
                <p className="text-xs text-slate-500 mt-1">
                  Stato attuale: <strong>{data.broker?.share_contacts_default ? "✅ Attivo" : "⛔ Disattivato"}</strong>
                </p>
              </div>
            </label>
          </section>
        )}

        <footer className="mt-12 text-center text-xs text-slate-400">
          Accesso effettuato come <span className="font-medium">{user?.email}</span>
        </footer>
      </main>
    </div>
  );
}
